import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  SALES_PRODUCT_TAB_ITEMS,
  type SalesProductTabId,
} from "../components/SalesProductAnalyticsTabsDashboard.data";
import {
  DEFAULT_SALES_ATTRIBUTION_REVENUE_SOURCE,
  type SalesAttributionDateRange,
  type SalesAttributionFilterState,
  type SalesAttributionRevenueSource,
} from "../utils/SalesAttributionTypes";

const DEFAULT_TAB: SalesProductTabId = "overview";
const DEFAULT_RANGE_DAYS = 30;
const DATE_FORMAT = "YYYY-MM-DD";
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const AD_PLATFORMS = ["google_ads", "meta_ads"];
const REVENUE_SOURCES: SalesAttributionRevenueSource[] = ["shopify", "ga4"];
const SCROLL_CONTAINER_SELECTOR = ".ant-layout-content";
const SCROLL_STORAGE_PREFIX = "sales-product-analytics:scroll:";

const PARAM = {
  tab: "tab",
  start: "start",
  end: "end",
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  platform: "platform",
  campaign: "campaign",
  revenue: "revenue",
} as const;

const ATTRIBUTION_PARAM_RESET: Record<string, null> = {
  [PARAM.utmSource]: null,
  [PARAM.utmMedium]: null,
  [PARAM.utmCampaign]: null,
  [PARAM.platform]: null,
  [PARAM.campaign]: null,
  [PARAM.revenue]: null,
};

type ParamUpdates = Record<string, string | null>;

const isIsoDate = (value: string | null): value is string =>
  value !== null && DATE_PATTERN.test(value) && dayjs(value, DATE_FORMAT).isValid();

const useParamUpdater = () => {
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (updates: ParamUpdates, replace = true) =>
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          Object.entries(updates).forEach(([key, value]) => {
            if (value) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          });
          return next;
        },
        { replace }
      ),
    [setSearchParams]
  );
};

export const useSalesProductTab = () => {
  const [searchParams] = useSearchParams();
  const updateParams = useParamUpdater();
  const rawTab = searchParams.get(PARAM.tab);
  const tab = SALES_PRODUCT_TAB_ITEMS.find(item => item.id === rawTab)?.id ?? DEFAULT_TAB;

  const setTab = useCallback(
    (next: SalesProductTabId) =>
      updateParams(
        { [PARAM.tab]: next === DEFAULT_TAB ? null : next, ...ATTRIBUTION_PARAM_RESET },
        false
      ),
    [updateParams]
  );

  return [tab, setTab] as const;
};

export const useSalesProductDateRange = () => {
  const [searchParams] = useSearchParams();
  const updateParams = useParamUpdater();
  const start = searchParams.get(PARAM.start);
  const end = searchParams.get(PARAM.end);

  const dateRange = useMemo<SalesAttributionDateRange>(() => {
    if (isIsoDate(start) && isIsoDate(end)) {
      return { startDate: start, endDate: end };
    }
    const today = dayjs();
    return {
      startDate: today.subtract(DEFAULT_RANGE_DAYS, "day").format(DATE_FORMAT),
      endDate: today.format(DATE_FORMAT),
    };
  }, [start, end]);

  const setDateRange = useCallback(
    (range: SalesAttributionDateRange) =>
      updateParams({ [PARAM.start]: range.startDate, [PARAM.end]: range.endDate }),
    [updateParams]
  );

  return [dateRange, setDateRange] as const;
};

export const useSalesAttributionUrlState = () => {
  const [searchParams] = useSearchParams();
  const updateParams = useParamUpdater();

  const utmSource = searchParams.get(PARAM.utmSource) ?? "";
  const utmMedium = searchParams.get(PARAM.utmMedium) ?? "";
  const utmCampaign = searchParams.get(PARAM.utmCampaign) ?? "";
  const campaign = searchParams.get(PARAM.campaign) ?? "";
  const rawPlatform = searchParams.get(PARAM.platform) ?? "";
  const rawRevenue = searchParams.get(PARAM.revenue);

  const filters = useMemo<SalesAttributionFilterState>(
    () => ({
      utmSource,
      utmMedium,
      utmCampaign,
      platform: AD_PLATFORMS.includes(rawPlatform) ? rawPlatform : "",
      campaign,
    }),
    [utmSource, utmMedium, utmCampaign, rawPlatform, campaign]
  );

  const revenueSource =
    REVENUE_SOURCES.find(source => source === rawRevenue) ??
    DEFAULT_SALES_ATTRIBUTION_REVENUE_SOURCE;

  const setFilters = useCallback(
    (next: SalesAttributionFilterState) =>
      updateParams({
        [PARAM.utmSource]: next.utmSource,
        [PARAM.utmMedium]: next.utmMedium,
        [PARAM.utmCampaign]: next.utmCampaign,
        [PARAM.platform]: next.platform,
        [PARAM.campaign]: next.campaign,
      }),
    [updateParams]
  );

  const setRevenueSource = useCallback(
    (next: SalesAttributionRevenueSource) =>
      updateParams({
        [PARAM.revenue]: next === DEFAULT_SALES_ATTRIBUTION_REVENUE_SOURCE ? null : next,
      }),
    [updateParams]
  );

  return { filters, setFilters, revenueSource, setRevenueSource };
};

const readStoredScroll = (key: string): number => {
  try {
    return Number(window.sessionStorage.getItem(key));
  } catch {
    return 0;
  }
};

const writeStoredScroll = (key: string, value: number) => {
  try {
    window.sessionStorage.setItem(key, String(value));
  } catch {
    return;
  }
};

export const useScrollRestoration = (
  anchorRef: RefObject<HTMLElement | null>,
  tab: SalesProductTabId,
  ready: boolean
) => {
  const storageKey = `${SCROLL_STORAGE_PREFIX}${tab}`;
  const readyRef = useRef(ready);
  const restoredRef = useRef(false);
  readyRef.current = ready;

  useEffect(() => {
    const container = anchorRef.current?.closest<HTMLElement>(SCROLL_CONTAINER_SELECTOR);
    if (!container) {
      return;
    }
    const save = () => {
      if (readyRef.current) {
        writeStoredScroll(storageKey, container.scrollTop);
      }
    };
    container.addEventListener("scroll", save, { passive: true });
    return () => container.removeEventListener("scroll", save);
  }, [anchorRef, storageKey]);

  useEffect(() => {
    if (!ready || restoredRef.current) {
      return;
    }
    restoredRef.current = true;
    const container = anchorRef.current?.closest<HTMLElement>(SCROLL_CONTAINER_SELECTOR);
    const saved = readStoredScroll(storageKey);
    if (container && Number.isFinite(saved) && saved > 0) {
      container.scrollTop = saved;
    }
  }, [ready, anchorRef, storageKey]);
};
